// Where Combobox's listbox opens (O451): below the field when the whole list
// fits there, above it when it fits there instead, and otherwise on the
// roomier side with its height capped to that room (the list scrolls). The
// room is measured against the visible viewport (`visualViewport`), which
// shrinks above the on-screen keyboard on phones.
//
// Flipping, not scrolling the page: the browser already scrolls a focused
// field above the on-screen keyboard, where the room is above the field, and
// a second, programmatic scroll on every new suggestion would move the field
// under the applicant's thumb and caret while they type. A flip moves only
// the panel; it needs no scroll room and no motion.

/** The panel's offset from the field: the module's `space-1-5`. */
export const LISTBOX_GAP = 6
/** Room kept between the panel and the visible viewport's edge. */
export const VIEWPORT_MARGIN = 8

export type ListboxSide = 'below' | 'above'

export interface ListboxPlacement {
  side: ListboxSide
  /** The cap in px when neither side holds the whole list; else null. */
  maxHeight: number | null
}

export interface PlacementInput {
  /** The field's box in client coordinates. */
  fieldTop: number
  fieldBottom: number
  /** The visible viewport in the same coordinates. */
  viewportTop: number
  viewportBottom: number
  /** The list's full height, borders included. */
  listHeight: number
}

export function getListboxPlacement({
  fieldTop,
  fieldBottom,
  viewportTop,
  viewportBottom,
  listHeight,
}: PlacementInput): ListboxPlacement {
  const below = viewportBottom - fieldBottom - LISTBOX_GAP - VIEWPORT_MARGIN
  const above = fieldTop - viewportTop - LISTBOX_GAP - VIEWPORT_MARGIN

  if (listHeight <= below) return {side: 'below', maxHeight: null}
  if (listHeight <= above) return {side: 'above', maxHeight: null}

  const side: ListboxSide = above > below ? 'above' : 'below'
  return {
    side,
    // Never more than the room, however little: a taller list would run
    // under the keyboard or off screen.
    maxHeight: Math.max(0, Math.floor(Math.max(above, below))),
  }
}

/** The visible viewport in client coordinates. */
export function getVisibleViewport(view: Window) {
  const visual = view.visualViewport
  return visual
    ? {top: visual.offsetTop, bottom: visual.offsetTop + visual.height}
    : {top: 0, bottom: view.innerHeight}
}
